import api, { objectToFormData, type FormDataValue } from './api';
import type { Plan, PlanFilters, ServiceType, ProductBrand } from '@app-types/models/plan';

interface PlansResponse {
  success: boolean;
  data: Plan[];
  message?: string;
}

interface PlanResponse {
  success: boolean;
  data: Plan;
  message?: string;
}

interface ServiceTypesResponse {
  success: boolean;
  data: ServiceType[];
  message?: string;
}

interface ProductBrandsResponse {
  success: boolean;
  data: ProductBrand[];
  message?: string;
}

export const getPlans = async (filters: PlanFilters = {}): Promise<PlansResponse> => {
  // Build query string from filters
  const queryParams = new URLSearchParams();

  if (filters.aiGenerated !== undefined) {
    queryParams.append('aiGenerated', String(filters.aiGenerated));
  }
  if (filters.isActive !== undefined) {
    queryParams.append('isActive', String(filters.isActive));
  }
  if (filters.staffId) {
    queryParams.append('staffId', filters.staffId);
  }
  if (filters.source) {
    queryParams.append('source', filters.source);
  }

  const queryString = queryParams.toString();
  const url = queryString ? `/business/plans?${queryString}` : '/business/plans';

  const response = await api.get<PlansResponse>(url);
  return response.data;
};

export const createPlan = async (plan: Record<string, unknown>): Promise<PlanResponse> => {
  const formData = objectToFormData(plan as Record<string, FormDataValue>);
  const response = await api.post<PlanResponse>('/business/plan', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const updatePlan = async (plan: Record<string, unknown> & { _id: string }): Promise<Plan> => {
  const formData = objectToFormData(plan as Record<string, FormDataValue>);
  const response = await api.put<Plan>(`/business/plan/${plan._id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deletePlan = async (id: string): Promise<unknown> => {
  const response = await api.delete<unknown>(`/business/plan/${id}`);
  return response.data;
};

export const getServiceTypes = async (): Promise<ServiceType[]> => {
  const response = await api.get<ServiceTypesResponse>('/business/service-types');
  // Accept both envelope ({success, data: [...]}) and bare shapes ({serviceTypes: [...]} or [...])
  const body = (response.data ?? response) as unknown as ServiceType[] | { data?: ServiceType[]; serviceTypes?: ServiceType[] };
  if (Array.isArray(body)) return body;
  if (Array.isArray((body as { data?: ServiceType[] }).data)) return (body as { data: ServiceType[] }).data;
  if (Array.isArray((body as { serviceTypes?: ServiceType[] }).serviceTypes)) return (body as { serviceTypes: ServiceType[] }).serviceTypes;
  return [];
};

export const getProductBrands = async (): Promise<ProductBrand[]> => {
  const response = await api.get<ProductBrandsResponse>('/business/product-brands');
  const body = (response.data ?? response) as unknown as ProductBrand[] | { data?: ProductBrand[]; productBrands?: ProductBrand[] };
  if (Array.isArray(body)) return body;
  if (Array.isArray((body as { data?: ProductBrand[] }).data)) return (body as { data: ProductBrand[] }).data;
  if (Array.isArray((body as { productBrands?: ProductBrand[] }).productBrands)) return (body as { productBrands: ProductBrand[] }).productBrands;
  return [];
};
